"use client"

/**
 * InPagePushAd — a simulated push-notification toast, sitewide (mounted
 * once in app/layout.tsx). Unlike real browser Push Notifications, this
 * needs no permission prompt or service worker — it's a self-contained,
 * self-positioning widget rendered entirely inside the ad network's own
 * script, same rendering approach as VideoSliderAd. Zone configured for
 * top-right positioning in the ExoClick dashboard, so the bounding box here
 * matches that corner.
 *
 * AUTO_DISMISS_MS — see the matching comment on InstantMessageAd.tsx for
 * the full reasoning. This zone's own delivered CSS (`.exo-ipp-container`,
 * pulled live from the actual creative) is a toast-notification format by
 * design, shorter-lived than the chat-message format, hence the shorter
 * timeout here.
 */
const AUTO_DISMISS_MS = 20_000
const NATIVE_W = 380
const NATIVE_H = 110

import { useEffect, useRef, useState } from "react"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { useVRStore } from "@/lib/vr-store"
import { useAdTier } from "@/lib/ad-tier"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { isAdCooldownExpired, isAdTestModeOn, markAdShown } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { useAdTriggerStore } from "@/lib/ad-trigger-store"
import { isSkipAdsModalActive } from "@/lib/skip-ads"
import { useCornerAdPlacement } from "@/lib/ad-placement"
import { useFixedCornerAdScale } from "@/lib/hooks/use-fixed-corner-ad-scale"
import { useTranslations } from "next-intl"

interface AdData {
  zone_id: string
  zone_type: string
  embed_code: string | null
}

export function InPagePushAd() {
  const t = useTranslations("inPagePushAd")
  const hasConsent = useHasConsent()
  // Suppressed entirely while in active VR mode (app/vr/page.tsx) — a fixed
  // corner widget popping up over a full-screen VR view has no sensible
  // place to render and would sit on top of the video.
  const isVRMode = useVRStore((s) => s.isVRMode)
  const { isPremiumUser } = useAdStore()
  const { scale, scaledWidth, scaledHeight } = useFixedCornerAdScale(NATIVE_W, NATIVE_H)
  // Force Ads test mode escape hatch — see the matching comment on
  // NetworkDisplayAd.tsx. Admin/super_admin accounts always read
  // isPremiumUser: true, so without this, Force Ads could never actually
  // force this widget to show for its only realistic tester.
  // See the matching comment on StickyBannerAd.tsx — SkipAdsModal's forced
  // trigger previously only bypassed the "enabled" check, not this premium
  // gate, so it silently rendered nothing for a premium/admin tester
  // outside Force Ads mode despite the modal genuinely triggering it.
  const effectivePremiumUser = (isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn() && !isSkipAdsModalActive()
  const adTier = useAdTier()
  const [adData, setAdData] = useState<AdData | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const triggerCount = useAdTriggerStore((s) => s.inPagePush)
  const lastTriggerRef = useRef(0)
  // Only allowed on the pages lib/ad-placement.ts lists. A manual harness
  // trigger or the Skip Ads modal shows it anywhere.
  const placementOk = useCornerAdPlacement("in_page_push")
  const forcedRef = useRef(false)

  useEffect(() => {
    const manuallyTriggered = triggerCount > lastTriggerRef.current
    lastTriggerRef.current = triggerCount
    if (manuallyTriggered) forcedRef.current = true
    if (!hasConsent || effectivePremiumUser || isVRMode) return
    if (!placementOk && !forcedRef.current && !isSkipAdsModalActive()) return
    // See the matching comment on StickyBannerAd.tsx / lib/skip-ads.ts. A
    // manual harness trigger bypasses this too, same reasoning.
    // Wait for the real config before reading the on/off switch and cooldown
    // -- see the matching comment on StickyBannerAd.tsx.
    let ignore = false
    useAdFrequencyStore.getState().fetchConfig().then(() => {
      if (ignore) return
      if (!manuallyTriggered && !useAdFrequencyStore.getState().isEnabled("in_page_push_ads") && !isSkipAdsModalActive()) return
      if (!manuallyTriggered) {
        const cooldown = useAdFrequencyStore.getState().getCooldownMinutes("in_page_push_ads")
        if (!isAdCooldownExpired("in_page_push", cooldown)) return
      }
      if (manuallyTriggered) setDismissed(false)

      fetch(`/api/ads/network-grid-ad?type=in_page_push&tier=${adTier}${isAdTestModeOn() ? "&preview=1" : ""}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (ignore) return
          if (d?.zone_id) {
            setAdData(d)
            markAdShown("in_page_push")
          }
        })
        .catch(() => {})
    })
    return () => {
      ignore = true
    }
  }, [hasConsent, effectivePremiumUser, adTier, triggerCount, placementOk])

  useEffect(() => {
    if (!adData) return
    const timer = setTimeout(() => setDismissed(true), AUTO_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [adData])

  if (!hasConsent || effectivePremiumUser || isVRMode || !adData || dismissed) return null
  if (!placementOk && !forcedRef.current && !isSkipAdsModalActive()) return null

  const zoneId = adData.zone_type === "js_embed" ? extractExoclickZoneId(adData.embed_code) : null
  if (!zoneId) return null

  return (
    <div
      className="fixed top-4 right-4 z-40 pointer-events-auto"
      style={{ width: scaledWidth, height: scaledHeight }}
    >
      {/* No close button of our own here on purpose — see the git history
          on this line for the two prior attempts (relocate the corner, then
          restyle it) that both turned out wrong. Live screenshots across
          multiple zones confirmed the ExoClick creative reliably paints its
          own real, working close button inside the iframe already; ours was
          never fixing a missing control, it was always just a second one
          stacked next to a real one. Removed rather than relocated again. */}
      <iframe
        src={exoclickIframeSrc(zoneId)}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        title={t("advertisement")}
        scrolling="no"
        // scale === 1 (desktop) renders exactly as before this mobile fix —
        // no transform at all. See the matching comment in VideoSliderAd.tsx
        // for why: a CSS transform forces GPU layer promotion, which isn't
        // worth the risk on the common path where no shrink is even needed.
        style={
          scale >= 1
            ? { width: "100%", height: "100%", border: 0, display: "block", background: "transparent" }
            : {
                width: NATIVE_W,
                height: NATIVE_H,
                border: 0,
                display: "block",
                background: "transparent",
                transform: `scale(${scale})`,
                transformOrigin: "top right",
              }
        }
      />
    </div>
  )
}
